import { default as React, useState, useMemo, useEffect } from 'react';
import {
  Grid,
  Typography,
  Box,
  InputBase,
  IconButton,
  Paper,
  makeStyles,
} from '@material-ui/core';
import Pagination from '@mui/material/Pagination';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import {
  useEntityList,
  entityRouteRef,
  entityRouteParams,
} from '@backstage/plugin-catalog-react';
import { isUnknownOwner } from './CatalogEntityCard';
import { useRouteRef } from '@backstage/core-plugin-api';
import { useNavigate } from 'react-router-dom';
import { CatalogEntityCard } from './CatalogEntityCard';

const CARDS_PER_PAGE = 20;

const useStyles = makeStyles(theme => ({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    padding: theme.spacing(0.5, 1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.background.paper,
    width: 280,
  },
  searchIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(1),
    fontSize: '1.2rem',
  },
  searchInput: {
    flex: 1,
    fontSize: '0.875rem',
  },
  clearButton: {
    padding: 4,
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8, 2),
    color: theme.palette.text.secondary,
  },
  paginationContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: theme.spacing(3),
  },
}));

const LEGACY_NAME_ANNOTATION = 'skystage.wellsky.com/legacy-name';

function entityMatchesSearch(entity: Entity, term: string): boolean {
  const lower = term.toLowerCase();
  const name = (
    entity.metadata.title ||
    entity.metadata.name ||
    ''
  ).toLowerCase();
  const description = (entity.metadata.description || '').toLowerCase();
  const type = (
    ((entity.spec as Record<string, unknown>)?.type as string) || ''
  ).toLowerCase();
  const lifecycle = (
    ((entity.spec as Record<string, unknown>)?.lifecycle as string) || ''
  ).toLowerCase();
  const tags = (entity.metadata.tags || []).join(' ').toLowerCase();
  const ownerRef =
    entity.relations?.find(r => r.type === RELATION_OWNED_BY)?.targetRef || '';
  const owner = isUnknownOwner(ownerRef) ? '' : ownerRef.toLowerCase();
  const system = (
    entity.relations?.find(r => r.type === RELATION_PART_OF)?.targetRef || ''
  ).toLowerCase();
  const legacyName = (
    entity.metadata?.annotations?.[LEGACY_NAME_ANNOTATION] || ''
  ).toLowerCase();

  return (
    name.includes(lower) ||
    description.includes(lower) ||
    type.includes(lower) ||
    lifecycle.includes(lower) ||
    tags.includes(lower) ||
    owner.includes(lower) ||
    system.includes(lower) ||
    legacyName.includes(lower)
  );
}

export const CatalogCardGrid = ({ title }: { title?: string }) => {
  const classes = useStyles();
  const { entities } = useEntityList();
  const navigate = useNavigate();
  const entityRoute = useRouteRef(entityRouteRef);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const filteredEntities = useMemo(() => {
    if (!searchTerm.trim()) return entities;
    return entities.filter(e => entityMatchesSearch(e, searchTerm.trim()));
  }, [entities, searchTerm]);

  const totalPages = Math.ceil(filteredEntities.length / CARDS_PER_PAGE);
  const paginatedEntities = useMemo(() => {
    const start = (page - 1) * CARDS_PER_PAGE;
    return filteredEntities.slice(start, start + CARDS_PER_PAGE);
  }, [filteredEntities, page]);

  // Reset to page 1 when entities change externally (e.g. sidebar filters)
  useEffect(() => {
    setPage(1);
  }, [entities]);

  // Reset to page 1 when search changes
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleCardClick = (entity: Entity) => {
    navigate(entityRoute(entityRouteParams(entity)));
  };

  const handlePageChange = (
    _event: React.ChangeEvent<unknown>,
    value: number,
  ) => {
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div>
      {/* Header with title and search */}
      <div className={classes.header}>
        <Typography variant="h6">
          {title
            ? `${title.replace(/\s*\(\d+\)$/, '')} (${filteredEntities.length})`
            : `All Entities (${filteredEntities.length})`}
        </Typography>
        <Paper elevation={0} className={classes.searchBox}>
          <SearchIcon className={classes.searchIcon} />
          <InputBase
            className={classes.searchInput}
            placeholder="Filter"
            value={searchTerm}
            onChange={e => handleSearchChange(e.target.value)}
          />
          {searchTerm && (
            <IconButton
              size="small"
              className={classes.clearButton}
              onClick={() => handleSearchChange('')}
            >
              <ClearIcon fontSize="small" />
            </IconButton>
          )}
        </Paper>
      </div>

      {/* Card grid or empty state */}
      {filteredEntities.length === 0 ? (
        <Box className={classes.emptyState}>
          <Typography variant="h6">No entities found</Typography>
          <Typography variant="body2">
            Try adjusting your search or filters.
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            {paginatedEntities.map(entity => {
              const key = `${entity.metadata.namespace || 'default'}/${
                entity.kind
              }/${entity.metadata.name}`;
              return (
                <Grid item xs={12} sm={6} md={4} key={key}>
                  <CatalogEntityCard
                    entity={entity}
                    onClick={handleCardClick}
                  />
                </Grid>
              );
            })}
          </Grid>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={classes.paginationContainer}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
              />
            </div>
          )}
        </>
      )}
    </div>
  );
};
